import { COUNTRY_NAMES, PAYMENT_CHANNELS, PAYMENT_PROVIDERS, PRODUCT_ORDER, SALE_KINDS, fmtDate } from "./constants";

export const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const shiftDay = (iso, days) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return isoDay(d);
};
export const todayIso = () => isoDay(new Date());
export const monthStart = (iso) => `${iso.slice(0, 7)}-01`;
export const daysBetween = (from, to) => Math.round((new Date(`${to}T00:00:00`) - new Date(`${from}T00:00:00`)) / 86400000) + 1;

// {from, to, country?} helpers – a single day is simply from === to; country narrows the venues to one country code
export const singleDay = (day) => ({ from: day, to: day });
export const isSingleDay = (r) => !!r && r.from === r.to;
export const clampRange = (r, min, max) => {
  let from = r.from < min ? min : r.from;
  let to = r.to > max ? max : r.to;
  if (from > to) [from, to] = [to, from];
  return { ...r, from, to };
};
const shortDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
export const fmtRange = (r) => (!r ? "" : isSingleDay(r) ? fmtDate(r.from) : `${shortDay(r.from)} – ${fmtDate(r.to)}`);
export const fmtRangeCountry = (r) =>
  !r ? "" : `${r.partner ? "Partners only · " : ""}${r.owner ? `${r.owner} · ` : ""}${r.country ? `${r.country === "ZZ" ? "No country" : COUNTRY_NAMES[r.country] || r.country} · ` : ""}${fmtRange(r)}`;

export const eventsInRange = (view, r) =>
  r
    ? (view?.recent_won || []).filter(
        (e) => e.won_day >= r.from && e.won_day <= r.to && (!r.country || e.country === r.country) && (!r.partner || !!e.partner) && (!r.owner || e.owner === r.owner)
      )
    : [];

// Builds a board view (same shape as views.since / views.all) holding only the venues onboarded in the range.
// Upsell rows (products sold to venues that were already clients) add products, never venues.
export const buildRangeView = (view, r) => {
  const events = eventsInRange(view, r);
  const countries = {};
  const products = Object.fromEntries(PRODUCT_ORDER.map((p) => [p, 0]));
  const byPipeline = Object.fromEntries(Object.keys(view.partners?.by_pipeline || {}).map((k) => [k, 0]));
  const byPartner = {};
  let productsSold = 0;
  let upsellProducts = 0;
  let partner = 0;
  let newVenues = 0;
  const upsellVenues = new Set();
  // Payments breakdown of the range (one entry per event that carries Payments)
  const pay = { total: 0, providers: {}, channels: {}, by_country: {}, venues: [] };
  // Sales mix of the range from each event's {product: [kinds]} (line-item list exists on the live board only)
  const mix = { kinds: Object.fromEntries(SALE_KINDS.map((k) => [k, 0])), matrix: {}, by_country: {}, venues: 0, venues_by_country: {}, items: null };
  for (const e of events) {
    const kinds = new Set();
    for (const [p, ks] of Object.entries(e.mix || {})) {
      for (const k of ks) {
        kinds.add(k);
        (mix.matrix[p] ||= {})[k] = (mix.matrix[p][k] || 0) + 1;
      }
    }
    if (kinds.size) {
      mix.venues += 1;
      mix.venues_by_country[e.country] = (mix.venues_by_country[e.country] || 0) + 1;
    }
    for (const k of kinds) {
      mix.kinds[k] += 1;
      (mix.by_country[e.country] ||= {})[k] = (mix.by_country[e.country][k] || 0) + 1;
    }
    const c = (countries[e.country] ||= { code: e.country, name: COUNTRY_NAMES[e.country] || e.country, total: 0, legacy: 0, won: 0, products_sold: 0, partner: 0, upsell_products: 0, upsell_venues: 0, products: {}, payments: null, ownerCounts: {} });
    if ((e.products || []).includes("Payments") && e.payment) {
      pay.total += 1;
      c.payments ||= { providers: {}, channels: {} };
      const bc = (pay.by_country[e.country] ||= { providers: {}, channels: {} });
      for (const p of e.payment.providers || []) { pay.providers[p] = (pay.providers[p] || 0) + 1; c.payments.providers[p] = (c.payments.providers[p] || 0) + 1; bc.providers[p] = (bc.providers[p] || 0) + 1; }
      for (const ch of e.payment.channels || []) { pay.channels[ch] = (pay.channels[ch] || 0) + 1; c.payments.channels[ch] = (c.payments.channels[ch] || 0) + 1; bc.channels[ch] = (bc.channels[ch] || 0) + 1; }
      pay.venues.push({ id: e.upsell ? e.venue_id : e.id, name: e.name, country: e.country, owner: e.owner, providers: e.payment.providers || [], channels: e.payment.channels || [], upsell: !!e.upsell, won_at: e.won_day, url: e.url || null });
    }
    if (e.upsell) {
      if (!upsellVenues.has(e.venue_id)) c.upsell_venues += 1;
      upsellVenues.add(e.venue_id);
    } else {
      newVenues += 1;
      c.total += 1;
      c.won += 1;
      if (e.owner) c.ownerCounts[e.owner] = (c.ownerCounts[e.owner] || 0) + 1;
      if (e.partner) {
        c.partner += 1;
        partner += 1;
        byPipeline[e.partner] = (byPipeline[e.partner] || 0) + 1;
        if (e.partner_name) byPartner[e.partner_name] = (byPartner[e.partner_name] || 0) + 1;
      }
    }
    for (const p of e.products || []) {
      c.products[p] = (c.products[p] || 0) + 1;
      c.products_sold += 1;
      products[p] = (products[p] || 0) + 1;
      productsSold += 1;
      if (e.upsell) {
        c.upsell_products += 1;
        upsellProducts += 1;
      }
    }
  }
  return {
    ...view,
    range: r,
    totals: { ...view.totals, total: newVenues, won: newVenues, legacy: 0, products_sold: productsSold, partner, upsell_products: upsellProducts, upsell_venues: upsellVenues.size },
    partners: {
      total: partner,
      by_pipeline: byPipeline,
      by_partner: Object.entries(byPartner).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
    },
    countries: Object.values(countries)
      .map(({ ownerCounts, ...c }) => {
        const owners = Object.entries(ownerCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
        return { ...c, owners, top_owner: owners[0]?.name || null };
      })
      .sort((a, b) => b.total - a.total || b.products_sold - a.products_sold || a.code.localeCompare(b.code)),
    products,
    payments: { ...pay, providers: Object.fromEntries(PAYMENT_PROVIDERS.filter((p) => pay.providers[p]).map((p) => [p, pay.providers[p]])), channels: Object.fromEntries(PAYMENT_CHANNELS.filter((c) => pay.channels[c]).map((c) => [c, pay.channels[c]])) },
    sales_mix: mix,
    latest_won: events,
  };
};
